import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { graphifyApi } from '@/api/graphify';

// 构建中（exporting/building）默认每 3s 轮询一次状态，也可通过参数覆盖
export const useGraphifyStatus = (refetchInterval?: number | false) => {
  return useQuery({
    queryKey: ['graphify-status'],
    queryFn: async () => {
      const response = await graphifyApi.getStatus();
      return response.data;
    },
    refetchInterval: refetchInterval ?? ((query) => {
      const s = query.state.data;
      return s && (s.state === 'exporting' || s.state === 'building') ? 3000 : false;
    }),
  });
};

export const useGraphifyGraph = (enabled = true, includeQuarantined = false) => {
  return useQuery({
    // 血泪#20：可变参数必须进缓存键，否则换参即拿旧缓存
    queryKey: ['graphify-graph', includeQuarantined ? 'with-quarantined' : 'main'],
    queryFn: async () => {
      const response = await graphifyApi.getGraph(includeQuarantined);
      return response.data;
    },
    staleTime: 60 * 1000,
    retry: false, // 未构建时后端返回 404，无需重试
    enabled,
  });
};

// 物理图（相似度图）：零 LLM、随时可取，未构建语义图也能看
// 【10-05 规模护栏】在数据入口截断渲染规模：2 万笔记 + 分组标签后
// 节点 21,816 / 边 25,404，2D Canvas 逐帧绘制与 3D lineSegments/Points
// 均把渲染进程主线程打满（真机实捕「点开图谱即卡死整个桌面端」）。
// 只截物理图（语义图是 LLM 抽取的稀疏图，节点本就少）。
// 按度数优先截断：保住有连接的节点，孤岛先丢；边同步裁剪防 forceLink
// source/target 悬空（悬空会抛错白屏 —— GraphNetworkPage 321 行血泪）。
// 10-11 用户拍板：2D/3D 两物理图节点上限 3000 → 5000。
export const MAX_GRAPH_NODES = 5000;

// 物理图原始拉取（单一事实源：trim/full 两个 hook 共享同一 queryKey 一次拉取，
// 截断走 select 派生——此前 full 独立键重复下载同一大 payload，10-08 拍板合并）
export const fetchPhysicalGraphRaw = async () => (await graphifyApi.getPhysicalGraph()).data;
// 服务端裁剪版（10-22：14.57MB 全量 payload 的最值一刀——前端本来就按度数裁 3000
// 渲染，裁在服务端全省传输/解析/建树；裁剪后 select 的 trimPhysicalGraph 近似 no-op）
export const fetchPhysicalGraphTrimmed = async () =>
  (await graphifyApi.getPhysicalGraph(MAX_GRAPH_NODES)).data;

// 度数截断 + 孤 tag 剔除（select 派生，纯函数）：2 万笔记 + 分组标签后
// 节点 21,816 / 边 25,404，2D Canvas 逐帧绘制与 3D lineSegments/Points
// 均把渲染进程主线程打满（真机实捕「点开图谱即卡死整个桌面端」）。
// 只截物理图（语义图是 LLM 抽取的稀疏图，节点本就少）。
// 按度数优先截断：保住有连接的节点，孤岛先丢；边同步裁剪防 forceLink
// source/target 悬空（悬空会抛错白屏 —— GraphNetworkPage 321 行血泪）。
type PhysicalGraphData = Awaited<ReturnType<typeof fetchPhysicalGraphRaw>>;
const trimPhysicalGraph = (g: PhysicalGraphData): PhysicalGraphData => {
  if (!g || !Array.isArray(g.nodes) || g.nodes.length <= MAX_GRAPH_NODES) return g;
  type Nd = (typeof g.nodes)[number];
  type Lk = (typeof g.links)[number];
  const deg: Record<string, number> = {};
  for (const l of g.links ?? []) {
    const s = String(typeof l.source === 'object' ? (l.source as Nd).id : l.source);
    const t = String(typeof l.target === 'object' ? (l.target as Nd).id : l.target);
    deg[s] = (deg[s] || 0) + 1;
    deg[t] = (deg[t] || 0) + 1;
  }
  const nodes = [...g.nodes]
    .sort((a, b) => (deg[b.id] || 0) - (deg[a.id] || 0))
    .slice(0, MAX_GRAPH_NODES);
  const keep = new Set(nodes.map((n) => n.id));
  const links = (g.links ?? []).filter((l: Lk) => {
    const s = String(typeof l.source === 'object' ? (l.source as Nd).id : l.source);
    const t = String(typeof l.target === 'object' ? (l.target as Nd).id : l.target);
    return keep.has(s) && keep.has(t);
  });
  // 10-05 晚实捕：截断后 943/1348 个 tag 的会员文档全被裁掉——它们在视图里
  // 零连接，只按后端 UMAP 坐标聚成一盘黄色噪音（2D）/球内浮点（3D）。
  // tag 是纯关系节点，成员不可见即无意义，从视图剔除（文档节点不剔：社区归属仍在）。
  const linkedIds = new Set<string>();
  for (const l of links) {
    const s = String(typeof l.source === 'object' ? (l.source as Nd).id : l.source);
    const t = String(typeof l.target === 'object' ? (l.target as Nd).id : l.target);
    linkedIds.add(s); linkedIds.add(t);
  }
  const nodes2 = nodes.filter((n) => n.file_type !== 'tag' || linkedIds.has(n.id));
  return { ...g, nodes: nodes2, links };
};

// 物理图缓存口径（10-08 拍板：换页不重新生成）——staleTime 30min，
// 内容变更不走 staleTime：content-version 戳失效链（invalidateContent 已含本键）
// + 30min 保底 sweep 兜漂移。换页挂载全部命中缓存零请求。
const PHYSICAL_STALE_MS = 30 * 60 * 1000;

export const usePhysicalGraph = (enabled = true) => {
  return useQuery({
    // 0.2.172 起与服务端裁剪版分键（full 版仍全量：待处理低度数节点不能被度数裁没，
    // 10-07 实捕）；主图拉 3000 裁剪版（~2MB），全量只在 subset 视图需要时拉
    queryKey: ['graphify-physical-graph', 't3000'],
    queryFn: fetchPhysicalGraphTrimmed,
    select: trimPhysicalGraph,
    staleTime: PHYSICAL_STALE_MS,
    retry: false,
    enabled,
  });
};

// 全量物理图（不做 MAX_GRAPH_NODES 截断）：子集图谱用——截断按度数裁点，
// 待处理新节点大多低度数会被裁没（10-07 实捕「300 多条待处理只有几十个渲染点」）。
// 0.2.172 起与裁剪版分键（服务端裁剪后两版不同源，不能再共享一次拉取）
export const usePhysicalGraphFull = (enabled = true) => {
  return useQuery({
    queryKey: ['graphify-physical-graph', 'full'],
    queryFn: fetchPhysicalGraphRaw,
    staleTime: PHYSICAL_STALE_MS,
    retry: false,
    enabled,
  });
};

export const useGraphifyBuild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preferred_model?: string) => {
      const response = await graphifyApi.build(preferred_model);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['graphify-status'] });
      queryClient.invalidateQueries({ queryKey: ['graphify-graph'] });
    },
  });
};

export const useGraphifyQuery = () => {
  return useMutation({
    mutationFn: async ({ question, preferred_model }: { question: string; preferred_model?: string }) => {
      const response = await graphifyApi.query(question, preferred_model);
      return response.data;
    },
  });
};

export const useGraphifyExplain = () => {
  return useMutation({
    mutationFn: async (node: string) => {
      const response = await graphifyApi.explain(node);
      return response.data;
    },
  });
};

export const useGraphifyReport = (enabled = true) => {
  return useQuery({
    queryKey: ['graphify-report'],
    queryFn: async () => {
      const response = await graphifyApi.getReport();
      return response.data;
    },
    staleTime: 60 * 1000,
    retry: false, // 未构建时后端返回 404，无需重试
    enabled,
  });
};
